import React, { useEffect, useState } from "react";
import { Box, Flex, Image, Text } from "@chakra-ui/react";
import { c } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { optionsOf, secondsLeft } from "./useLiveActivity";

/** Filled buttons, in the two kinds Stande uses: ink, and blue for the one
 *  thing on a screen that starts something. */
export const inkButton = {
  bg: c.solidBg,
  color: c.solidText,
  _hover: { bg: c.solidBg, opacity: 0.86 },
  _active: { bg: c.solidBg, opacity: 0.8 },
  _disabled: { opacity: 0.4, cursor: "not-allowed" },
};

export const blueButton = {
  bg: STANDE.primaryBlue,
  color: STANDE.black,
  _hover: { bg: STANDE.primaryBlue, filter: "brightness(0.94)" },
  _active: { bg: STANDE.primaryBlue, filter: "brightness(0.9)" },
  _disabled: { opacity: 0.4, cursor: "not-allowed" },
};

export const quietButton = {
  bg: c.surface,
  color: c.text,
  border: "1px solid",
  borderColor: c.borderSoft,
  _hover: { bg: c.hover },
};

export const Chip = ({ children, tone = "grey", ...rest }) => {
  const tones = {
    grey: { bg: c.surfaceSubtle, color: c.textSub, borderColor: c.borderSoft },
    blue: { bg: c.infoSoft, color: c.infoText, borderColor: "transparent" },
    ink: { bg: c.solidBg, color: c.solidText, borderColor: "transparent" },
    green: { bg: STANDE.lightGreen, color: STANDE.black, borderColor: "transparent" },
    yellow: { bg: STANDE.lightYellow, color: STANDE.black, borderColor: "transparent" },
    red: { bg: STANDE.lightRed, color: STANDE.black, borderColor: "transparent" },
  };
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="4px"
      px="8px"
      h="22px"
      borderRadius="200px"
      fontSize="11px"
      whiteSpace="nowrap"
      border="1px solid"
      {...tones[tone]}
      {...rest}
    >
      {children}
    </Box>
  );
};

export const Initial = ({ name, size = "28px" }) => (
  <Flex
    boxSize={size}
    borderRadius="full"
    align="center"
    justify="center"
    bg={c.surfaceSubtle}
    border="1px solid"
    borderColor={c.borderSoft}
    color={c.text}
    fontSize="11px"
    fontWeight="600"
    flexShrink={0}
  >
    {(name || "?").trim()[0]?.toUpperCase()}
  </Flex>
);

/** A clock that ticks once a second, on the server's time. */
export const useTick = (clock, active = true) => {
  const [now, setNow] = useState(() => clock());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(clock()), 1000);
    return () => clearInterval(id);
  }, [clock, active]);
  return now;
};

export const formatSeconds = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** The countdown on the open question. Ink, and red for the last ten. */
export const Countdown = ({ activity, clock, size = "md" }) => {
  const now = useTick(clock, Boolean(activity?.timer_seconds));
  const left = secondsLeft(activity, now);
  if (left == null) return null;
  const big = size === "lg";
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      px={big ? "18px" : "12px"}
      py={big ? "10px" : "5px"}
      borderRadius={big ? "16px" : "12px"}
      bg={left <= 10 ? STANDE.darkRed : c.solidBg}
      color={left <= 10 ? STANDE.cream : c.solidText}
      minW={big ? "110px" : "64px"}
      transition="background 0.3s"
      aria-live="polite"
    >
      {big && (
        <Text fontSize="10px" letterSpacing="0.12em" opacity={0.7}>
          TIME LEFT
        </Text>
      )}
      <Text fontSize={big ? "30px" : "15px"} fontWeight="600" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatSeconds(left)}
      </Text>
    </Flex>
  );
};

/**
 * What a question says: its words, its figure, and — for multiple choice —
 * its options as buttons. `onPick` makes the options answerable; without
 * it they are only shown.
 */
export const QuestionBody = ({ question, size = "md", picked, onPick, disabled }) => {
  const options = question.is_mcq ? optionsOf(question.answer_format) : [];
  const big = size === "lg";
  return (
    <Box>
      <Text fontSize={big ? "22px" : "14.5px"} lineHeight="1.4" fontWeight={big ? "500" : "400"} color={c.text} whiteSpace="pre-wrap">
        {question.question_text}
      </Text>
      {question.image_1 && (
        <Image src={question.image_1} alt="" mt="12px" borderRadius="10px" maxH={big ? "320px" : "220px"} objectFit="contain" bg="white" />
      )}
      {options.length > 0 && (
        <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="8px" mt="14px">
          {options.map((o) => {
            const isPicked = picked === o.label;
            return (
              <Flex
                key={o.label}
                as={onPick ? "button" : "div"}
                type={onPick ? "button" : undefined}
                onClick={onPick && !disabled ? () => onPick(o.label) : undefined}
                align="center"
                gap="10px"
                p="10px 12px"
                borderRadius="12px"
                textAlign="left"
                bg={isPicked ? c.solidBg : c.surface}
                color={isPicked ? c.solidText : c.text}
                border="1px solid"
                borderColor={isPicked ? c.solidBg : c.borderSoft}
                cursor={onPick && !disabled ? "pointer" : "default"}
                opacity={disabled && !isPicked ? 0.6 : 1}
                _hover={onPick && !disabled && !isPicked ? { borderColor: c.textMuted } : {}}
                transition="background 0.12s, border-color 0.12s"
              >
                <Flex
                  boxSize="26px"
                  borderRadius="full"
                  align="center"
                  justify="center"
                  fontSize="12px"
                  fontWeight="600"
                  flexShrink={0}
                  bg={isPicked ? c.solidText : c.infoSoft}
                  color={isPicked ? c.solidBg : c.infoText}
                >
                  {o.label}
                </Flex>
                <Text fontSize="13px">{o.text || `Option ${o.label}`}</Text>
              </Flex>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/** "3/4" on the tint its result earns: full, part, or none. */
export const MarkPill = ({ mark, total }) => {
  if (mark == null) return null;
  const tone = mark >= total ? "green" : mark > 0 ? "yellow" : "red";
  return (
    <Chip tone={tone} fontSize="12px" fontWeight="600" h="24px" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {mark}/{total}
    </Chip>
  );
};

/** A small caps heading, as the prototypes had — quieter here. */
export const Eyebrow = ({ children, ...rest }) => (
  <Text fontSize="10.5px" letterSpacing="0.12em" textTransform="uppercase" color={c.textMuted} fontWeight="600" {...rest}>
    {children}
  </Text>
);
